import React, {
  useState,
  useRef,
  useCallback,
  ChangeEvent,
  useEffect,
} from 'react'
import TextField from '@mui/material/TextField'
import InputAdornment from '@mui/material/InputAdornment'
import SearchIcon from '@mui/icons-material/Search'
import ClearIcon from '@mui/icons-material/Clear'
import IconButton from '@mui/material/IconButton'
import FormControlLabel from '@mui/material/FormControlLabel'

import {
  handlePaste,
  ClipboardEventInput,
  handleInputChange,
  clearSearch,
} from './SearchInputUtils'
import { useAppSelector } from 'redux/Hook'

interface SearchInputProps {
  onSearch: (value: string) => void
}

const SearchInput = ({ onSearch }: SearchInputProps) => {
  const {
    queries: { search },
  } = useAppSelector((state: any) => state.textAnalysis.main)

  const searchValue = search?.[0] ?? ''

  const inputRef = useRef<HTMLInputElement | null>(null)
  const [inputValue, setInputValue] = useState(searchValue)
  const [clearable, setClearable] = useState(false)

  useEffect(() => {
    setInputValue(searchValue)
    setClearable(!!searchValue)
  }, [searchValue])

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      setInputValue(value)
      handleInputChange(value, onSearch, setClearable)
    },
    [onSearch],
  )

  const handlePasteCallback = useCallback((e: ClipboardEventInput) => {
    handlePaste(e)
  }, [])

  const handleClearSearch = useCallback(() => {
    setInputValue('')
    clearSearch(inputRef, onSearch, setClearable)
  }, [onSearch])

  return (
    <FormControlLabel
      sx={{
        margin: 0,
        width: { xs: '100%', md: 'auto' },
        '.MuiFormControlLabel-label': {
          display: 'none',
        },
      }}
      control={
        <TextField
          inputRef={inputRef}
          value={inputValue}
          data-testid="search-input-contribution"
          size="small"
          onChange={handleChange}
          onPaste={handlePasteCallback}
          placeholder="Search keywords"
          autoComplete="off"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="inherit" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                {clearable && (
                  <IconButton
                    size="small"
                    onClick={handleClearSearch}
                    aria-label="clear search"
                  >
                    <ClearIcon fontSize="inherit" />
                  </IconButton>
                )}
              </InputAdornment>
            ),
          }}
        />
      }
      label="Search contributions"
    />
  )
}

export { SearchInput }
